<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      {{ $t('importSummary') }}
      <v-spacer/>
      <BaseDialogActions hideRefresh @close="$emit('close')">
        <template #left>
          <Icon
            icon="mdi-download"
            iconColor="white"
            :tooltipText="$t('downloadSummary')"
            :small="false"
            @icon-clicked="downloadSummary"
          />
        </template>
      </BaseDialogActions>
    </v-card-title>
    <v-card-text class="pt-4">
      <v-row dense>
        <v-col cols="auto" v-if="summaryData.totalGrossVolume > 0 || summaryData.totalNetVolume > 0">
          <v-chip class="ma-1" color="black" text-color="white">
            <Icon
              icon="mdi-ruler-square"
              iconColor="white"
            />
            {{ $t('grossVolume') }} {{ numberWithCommas(summaryData.totalGrossVolume, 0) }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="summaryData.totalGrossVolume > 0 || summaryData.totalNetVolume > 0">
          <v-chip class="ma-1" color="black" text-color="white">
            <Icon
              icon="mdi-ruler-square"
              iconColor="white"
            />
            {{ $t('netVolume') }} {{ numberWithCommas(summaryData.totalNetVolume, 0) }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="summaryData.failedGrossVolume > 0 || summaryData.failedNetVolume > 0">
          <v-chip class="ma-1" color="black" text-color="white">
            <Icon
              icon="mdi-ruler-square"
              iconColor="white"
            />
            {{ $t('failedGrossVolume') }} {{ numberWithCommas(summaryData.failedGrossVolume, 0) }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="summaryData.failedGrossVolume > 0 || summaryData.failedNetVolume > 0">
          <v-chip class="ma-1" color="black" text-color="white">
            <Icon
              icon="mdi-ruler-square"
              iconColor="white"
            />
            {{ $t('failedNetVolume') }} {{ numberWithCommas(summaryData.failedNetVolume, 0) }}
          </v-chip>
        </v-col>
      </v-row>
      <v-tabs v-model="currentTab" class="mt-2 mb-2">
        <v-tab v-if="hasLogsTickets">{{ $t('logs') }} ({{ logsSuccessful.length + logsFailed.length }})</v-tab>
        <v-tab v-if="hasLysTickets">{{ $t('logYardSale') }} ({{ lysSuccessful.length + lysFailed.length }})</v-tab>
      </v-tabs>
      <v-tabs-items v-model="currentTab">
        <!-- Logs Tab -->
        <v-tab-item v-if="hasLogsTickets">
          <v-expansion-panels v-model="logsPanel" accordion class="mt-1">
            <v-expansion-panel v-if="logsSuccessful.length > 0">
              <v-expansion-panel-header :style="{ height: $vuetify.breakpoint.xl ? '48px' : $vuetify.breakpoint.mdAndUp ? '64px' : $vuetify.breakpoint.smAndUp ? '100px' : '200px' }">
                <v-row no-gutters align="center">
                  <v-col cols="auto">
                    <Icon
                      class="mr-2"
                      icon="mdi-check-circle"
                      iconColor="success"
                      :small="false"
                    />
                  </v-col>
                  <v-col>
                    <span class="font-weight-medium">{{ $t('successful') }} ({{ logsSuccessful.length }})</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <DataTable
                  v-if="logsSuccessful.length > 0"
                  :items="logsSuccessful"
                  :headers="successColumnSetLogs"
                  :customCells="successCustomColumns"
                  class="pt-2"
                >
                  <template #ticketTypeLabel="{ item }">
                    <Icon
                      v-if="item.ticketType?.value === ScaleSheetTicketType.IncludesVolume.value"
                      icon="mdi-ruler-square"
                      :tooltipText="$t('scaled')"
                      :small="false"
                      iconColor="success"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.WeightBased.value"
                      icon="mdi-weight"
                      :tooltipText="$t('weightBased')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.Expanded.value"
                      icon="mdi-alpha-e-circle-outline"
                      :tooltipText="$t('expansion')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                  </template>
                </DataTable>
                <v-row v-else justify="center" class="mt-2 mb-2">
                  <v-col cols="auto">
                    <span class="subtitle-2 grey--text">{{ $t('noTicketsToDisplay') }}</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="logsFailed.length > 0">
              <v-expansion-panel-header :style="{ height: $vuetify.breakpoint.xl ? '48px' : $vuetify.breakpoint.mdAndUp ? '64px' : $vuetify.breakpoint.smAndUp ? '100px' : '200px' }">
                <v-row no-gutters align="center">
                  <v-col cols="auto">
                    <Icon
                      class="mr-2"
                      icon="mdi-alert-circle"
                      iconColor="error"
                      :small="false"
                    />
                  </v-col>
                  <v-col>
                    <span class="font-weight-medium">{{ $t('failed') }} ({{ logsFailed.length }})</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <DataTable
                  v-if="logsFailed.length > 0"
                  :items="logsFailed"
                  :headers="failedColumnSetLogs"
                  :customCells="failedCustomColumns"
                  class="pt-2"
                >
                  <template #ticketTypeLabel="{ item }">
                    <Icon
                      v-if="item.ticketType?.value === ScaleSheetTicketType.IncludesVolume.value"
                      icon="mdi-ruler-square"
                      :tooltipText="$t('scaled')"
                      :small="false"
                      iconColor="success"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.WeightBased.value"
                      icon="mdi-weight"
                      :tooltipText="$t('weightBased')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.Expanded.value"
                      icon="mdi-alpha-e-circle-outline"
                      :tooltipText="$t('expansion')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                  </template>
                  <template #error="{ item }">
                    <span class="error--text body-2">{{ item.error }}</span>
                  </template>
                </DataTable>
                <v-row v-else justify="center" class="mt-2 mb-2">
                  <v-col cols="auto">
                    <span class="subtitle-2 grey--text">{{ $t('noFailedTickets') }}</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
        <!-- Log Yard Sale Tab -->
        <v-tab-item v-if="hasLysTickets">
          <v-expansion-panels v-model="lysPanel" accordion class="mt-1">
            <v-expansion-panel v-if="lysSuccessful.length > 0">
              <v-expansion-panel-header :style="{ height: $vuetify.breakpoint.xl ? '48px' : $vuetify.breakpoint.mdAndUp ? '64px' : $vuetify.breakpoint.smAndUp ? '100px' : '200px' }">
                <v-row no-gutters align="center">
                  <v-col cols="auto">
                    <Icon
                      class="mr-2"
                      icon="mdi-check-circle"
                      iconColor="success"
                      :small="false"
                    />
                  </v-col>
                  <v-col>
                    <span class="font-weight-medium">{{ $t('successful') }} ({{ lysSuccessful.length }})</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <DataTable
                  v-if="lysSuccessful.length > 0"
                  :items="lysSuccessful"
                  :headers="successColumnSetLys"
                  :customCells="successCustomColumns"
                  class="pt-2"
                >
                  <template #ticketTypeLabel="{ item }">
                    <Icon
                      v-if="item.ticketType?.value === ScaleSheetTicketType.IncludesVolume.value"
                      icon="mdi-ruler-square"
                      :tooltipText="$t('scaled')"
                      :small="false"
                      iconColor="success"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.WeightBased.value"
                      icon="mdi-weight"
                      :tooltipText="$t('weightBased')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.Expanded.value"
                      icon="mdi-alpha-e-circle-outline"
                      :tooltipText="$t('expansion')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                  </template>
                </DataTable>
                <v-row v-else justify="center" class="mt-2 mb-2">
                  <v-col cols="auto">
                    <span class="subtitle-2 grey--text">{{ $t('noTicketsToDisplay') }}</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="lysFailed.length > 0">
              <v-expansion-panel-header :style="{ height: $vuetify.breakpoint.xl ? '48px' : $vuetify.breakpoint.mdAndUp ? '64px' : $vuetify.breakpoint.smAndUp ? '100px' : '200px' }">
                <v-row no-gutters align="center">
                  <v-col cols="auto">
                    <Icon
                      class="mr-2"
                      icon="mdi-alert-circle"
                      iconColor="error"
                      :small="false"
                    />
                  </v-col>
                  <v-col>
                    <span class="font-weight-medium">{{ $t('failed') }} ({{ lysFailed.length }})</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <DataTable
                  v-if="lysFailed.length > 0"
                  :items="lysFailed"
                  :headers="failedColumnSetLys"
                  :customCells="failedCustomColumns"
                  class="pt-2"
                >
                  <template #ticketTypeLabel="{ item }">
                    <Icon
                      v-if="item.ticketType?.value === ScaleSheetTicketType.IncludesVolume.value"
                      icon="mdi-ruler-square"
                      :tooltipText="$t('scaled')"
                      :small="false"
                      iconColor="success"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.WeightBased.value"
                      icon="mdi-weight"
                      :tooltipText="$t('weightBased')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                    <Icon
                      v-else-if="item.ticketType?.value === ScaleSheetTicketType.Expanded.value"
                      icon="mdi-alpha-e-circle-outline"
                      :tooltipText="$t('expansion')"
                      :small="false"
                      iconColor="grey darken-2"
                    />
                  </template>
                  <template #error="{ item }">
                    <span class="error--text body-2">{{ item.error }}</span>
                  </template>
                </DataTable>
                <v-row v-else justify="center" class="mt-2 mb-2">
                  <v-col cols="auto">
                    <span class="subtitle-2 grey--text">{{ $t('noFailedTickets') }}</span>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
import TicketHeaders from '@/headers/Ticket'
import { ContractType, ScaleSheetTicketType } from '@/utils/Enumerations.js'
import { numberWithCommas } from '@/utils/NumericMutations.js'
import moment from 'moment'
export default {
  name: 'ImportSummaryDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    summaryData: {
      type: Object,
      required: true
    },
    fileName: {
      type: String,
      default: 'import'
    }
  },

  data: () => ({
    currentTab: 0,
    logsPanel: 0,
    lysPanel: 0,
    successColumnSetLogs: undefined,
    successColumnSetLys: undefined,
    failedColumnSetLogs: undefined,
    failedColumnSetLys: undefined,
    ScaleSheetTicketType,
    numberWithCommas
  }),

  created () {
    this.successColumnSetLogs = this.importSummarySuccessHeadersLogs.getColumns()
    this.successColumnSetLys = this.importSummarySuccessHeadersLys.getColumns()
    this.failedColumnSetLogs = this.importSummaryFailedHeadersLogs.getColumns()
    this.failedColumnSetLys = this.importSummaryFailedHeadersLys.getColumns()
    this.currentTab = this.hasLogsTickets ? 0 : 1
  },

  computed: {
    importSummarySuccessHeadersLogs () {
      return TicketHeaders.importSummarySuccessHeaders(ContractType.Production.value)
    },
    importSummarySuccessHeadersLys () {
      return TicketHeaders.importSummarySuccessHeaders(ContractType.LogYardSale.value)
    },
    importSummaryFailedHeadersLogs () {
      return TicketHeaders.importSummaryFailedHeaders(ContractType.Production.value)
    },
    importSummaryFailedHeadersLys () {
      return TicketHeaders.importSummaryFailedHeaders(ContractType.LogYardSale.value)
    },
    logsSuccessful () {
      return this.summaryData.successfulTickets.filter(t => t.contractType === ContractType.Production.value || t.contractType === ContractType.WoodsSale.value)
    },
    logsFailed () {
      return this.summaryData.failedTickets.filter(t => t.contractType === ContractType.Production.value || t.contractType === ContractType.WoodsSale.value)
    },
    lysSuccessful () {
      return this.summaryData.successfulTickets.filter(t => t.contractType === ContractType.LogYardSale.value)
    },
    lysFailed () {
      return this.summaryData.failedTickets.filter(t => t.contractType === ContractType.LogYardSale.value)
    },
    hasLogsTickets () {
      return this.logsSuccessful.length > 0 || this.logsFailed.length > 0
    },
    hasLysTickets () {
      return this.lysSuccessful.length > 0 || this.lysFailed.length > 0
    },
    successCustomColumns () {
      return [
        { slotName: 'ticketTypeLabel', value: 'ticketTypeLabel' }
      ]
    },
    failedCustomColumns () {
      return [
        { slotName: 'ticketTypeLabel', value: 'ticketTypeLabel' },
        { slotName: 'error', value: 'error' }
      ]
    }
  },

  methods: {
    getContractTypeLabel (contractType) {
      if (contractType === ContractType.LogYardSale.value) return this.$t('logYardSale')
      return this.$t('logs')
    },
    downloadSummary () {
      const baseName = this.fileName.includes('.') ? this.fileName.split('.').slice(0, -1).join('') : this.fileName
      const header = `${this.$t('ticketNumber')},${this.$t('contractType')},${this.$t('status')},${this.$t('grossVolume')},${this.$t('netVolume')},${this.$t('error')}`
      const successRows = this.summaryData.successfulTickets.map(t =>
        `"${t.ticketNumber ?? 'N/A'}","${this.getContractTypeLabel(t.contractType)}","${this.$t('successful')}","${t.grossVolume?.toFixed(2) ?? '0.00'}","${t.netVolume?.toFixed(2) ?? '0.00'}",""`
      )
      const failedRows = this.summaryData.failedTickets.map(t =>
        `"${t.ticketNumber ?? 'N/A'}","${this.getContractTypeLabel(t.contractType)}","${this.$t('failed')}","${t.grossVolume?.toFixed(2) ?? '0.00'}","${t.netVolume?.toFixed(2) ?? '0.00'}","${String(t.error ?? '').replace(/"/g, '""')}"`
      )
      const csv = [header, ...successRows, ...failedRows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Import_Summary_${moment().format('YYYYMMDD_HHmmss')}_${baseName}.csv`
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>
